import { PrimaryButton, TertiaryButton, Flex } from '@workday/canvas-kit-react';
import { autoId } from '@workday/upc-common';
import React, { useEffect } from 'react';
import styles from './TOTPVerficationForm.module.scss';
import { useTranslation } from 'react-i18next';
import { ID_CANCEL_BUTTON, ID_VERIFY_OTP_BUTTON } from '../../autoIds';
import { useForm } from 'react-hook-form';
import { OTPFormField, PasscodeFormState } from '../otp-form-field/otp-form-field';
import { useErrorHandling } from '../../hooks/useErrorHandling';
import { serverError } from '../../utils/server-error';
import { UseMutationStateResult } from '@reduxjs/toolkit/dist/query/react/buildHooks';

export interface TOTPVerificationFormProps {
    disabled?: boolean;
    mutation?: UseMutationStateResult<any, any>;
    onSubmit?: (values: any) => void;
    onNext?: () => void;
    onCancel?: () => void;
}

export function TOTPVerificationForm({
    disabled = false,
    onSubmit,
    onNext,
    onCancel,
    mutation
}: TOTPVerificationFormProps) {
    const { control, setError, handleSubmit } = useForm<PasscodeFormState>({
        defaultValues: {
            passcode: ''
        }
    });

    useErrorHandling(mutation.error, {
        inlineErrors: ['UPC-1005', 'UPC-1054'],
        onInlineError: error => setError('passcode', serverError(error))
    });

    useEffect(() => {
        if (mutation.isSuccess && onNext) {
            onNext();
        }
    }, [onNext, mutation.isSuccess]);

    const { t } = useTranslation();

    function onSubmitHandler(values) {
        const { passcode } = values;

        if (onSubmit) {
            onSubmit({
                passcode
            });
        }
    }

    return (
        <Flex flexDirection='column' alignItems='center'>
            <h1 className={styles.title}>{t('TOTPVerificationForm.letsVerify')}</h1>
            <form onSubmit={handleSubmit(onSubmitHandler)} className={styles.form}>
                <OTPFormField
                    className={styles.otpFormField}
                    control={control}
                    autoFocus={true}
                    legend={t('TOTPVerificationForm.toVerifyYourIdentity')}
                    legendProps={{ className: styles.body }}
                    legendVisible={true}
                    disabled={disabled || mutation.isLoading}
                />
                <Flex flexDirection='column' maxWidth='21rem' alignItems='center'>
                    <PrimaryButton
                        {...autoId(ID_VERIFY_OTP_BUTTON)}
                        className={styles.submit}
                        disabled={mutation.isLoading || disabled}
                        size='large'
                        type='submit'
                        marginBottom='1rem'>
                        {t('TOTPVerificationForm.submitButton')}
                    </PrimaryButton>
                    {onCancel ? (
                        <TertiaryButton {...autoId(ID_CANCEL_BUTTON)} type='button' size='large' onClick={onCancel}>
                            {t('Common.cancel', { ns: 'shared' })}
                        </TertiaryButton>
                    ) : null}
                </Flex>
            </form>
        </Flex>
    );
}
